import React, { useRef } from "react";
import { useTheme } from "../context/ThemeContext";
import Statistics from "../components/Statistics";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { calculateProfit } from "../utils/tradeCalculations";
import AIAnalysis from "../components/AIAnalysis";

function BacktestResults({
  initialDeposit,
  calculateCurrentBalance,
  trades,
  handleReset,
}) {
  const { darkMode } = useTheme();
  const contentRef = useRef(null);

  const exportToPDF = async () => {
    const content = contentRef.current;

    // Create PDF with A4 dimensions
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    // A4 dimensions
    const pageWidth = 210;
    const pageHeight = 297;
    const margins = { top: 10, right: 10, bottom: 10, left: 10 };

    try {
      // Capture the content
      const canvas = await html2canvas(content, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: darkMode ? "#111827" : "#ffffff",
        windowWidth: content.scrollWidth,
        windowHeight: content.scrollHeight,
        onclone: (clonedDoc) => {
          // Modify the clone to optimize for PDF
          const clonedContent = clonedDoc.querySelector("[data-clone-content]");
          if (clonedContent) {
            // Adjust font sizes for better fit
            clonedContent.style.fontSize = "0.9em";
            // Remove hover effects and animations
            const elements =
              clonedContent.getElementsByClassName("hover:scale-105");
            for (let el of elements) {
              el.classList.remove("hover:scale-105");
            }
          }
        },
      });

      // Calculate dimensions to fit on one page
      const contentWidth = pageWidth - margins.left - margins.right;
      const contentHeight = pageHeight - margins.top - margins.bottom;

      // Calculate scale to fit width
      const scale = contentWidth / canvas.width;
      const scaledHeight = canvas.height * scale;

      // If scaled height is too large, scale down further
      const finalScale =
        scaledHeight > contentHeight ? contentHeight / canvas.height : scale;
      const finalWidth = canvas.width * finalScale;
      const finalHeight = canvas.height * finalScale;

      // Center the content
      const xOffset = (pageWidth - finalWidth) / 2;
      const yOffset = (pageHeight - finalHeight) / 2;

      // Add the image to PDF
      pdf.addImage(
        canvas,
        "PNG",
        xOffset,
        yOffset,
        finalWidth,
        finalHeight,
        undefined,
        "FAST"
      );

      // Add footer
      pdf.setFontSize(8);
      pdf.setTextColor(128);
      const timestamp = new Date().toLocaleString();
      pdf.text(`Generated on ${timestamp}`, margins.left, pageHeight - 5);
      pdf.text("Page 1/1", pageWidth - margins.right - 20, pageHeight - 5);

      // Save the PDF
      pdf.save(`backtest-results-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      // You might want to show an error message to the user here
    }
  };

  const calculateWinRate = () => {
    if (trades.length === 0) return 0;
    const winningTrades = trades.filter(
      (trade) => trade.outcome === "tp"
    ).length;
    return ((winningTrades / trades.length) * 100).toFixed(2);
  };

  const calculateTotalTrades = () => {
    return trades.length;
  };

  const generateStrategyAnalysis = () => {
    // Base calculations
    const winRate = parseFloat(calculateWinRate());
    const buyTrades = trades.filter((trade) => trade.entryType === "buy");
    const sellTrades = trades.filter((trade) => trade.entryType === "sell");
    const winningTrades = trades.filter((trade) => trade.outcome === "tp");
    const losingTrades = trades.filter((trade) => trade.outcome === "sl");

    // Calculate win rates for buy and sell trades
    const buyWinRate =
      buyTrades.length > 0
        ? (buyTrades.filter((trade) => trade.outcome === "tp").length /
            buyTrades.length) *
          100
        : 0;
    const sellWinRate =
      sellTrades.length > 0
        ? (sellTrades.filter((trade) => trade.outcome === "tp").length /
            sellTrades.length) *
          100
        : 0;

    // Calculate total profit/loss
    const totalProfit = calculateCurrentBalance() - initialDeposit;

    // Calculate average profit per winning trade and average loss per losing trade
    const averageWin =
      winningTrades.length > 0
        ? winningTrades.reduce(
            (sum, trade) => sum + calculateProfit(trade),
            0
          ) / winningTrades.length
        : 0;

    const averageLoss =
      losingTrades.length > 0
        ? Math.abs(
            losingTrades.reduce((sum, trade) => sum + calculateProfit(trade), 0)
          ) / losingTrades.length
        : 0;

    // Calculate profit factor (total wins / total losses)
    const totalWins = winningTrades.reduce(
      (sum, trade) => sum + calculateProfit(trade),
      0
    );
    const totalLosses = Math.abs(
      losingTrades.reduce((sum, trade) => sum + calculateProfit(trade), 0)
    );
    const profitFactor =
      totalLosses > 0
        ? (totalWins / totalLosses).toFixed(2)
        : totalWins.toFixed(2);

    // Calculate win streaks
    let currentStreak = 0;
    let maxWinStreak = 0;

    trades.forEach((trade, index) => {
      if (trade.outcome === "tp") {
        currentStreak++;
        maxWinStreak = Math.max(maxWinStreak, currentStreak);
      } else {
        currentStreak = 0;
      }
    });

    // Calculate average RR (Risk/Reward) ratio
    const averageRR =
      trades.reduce((sum, trade) => {
        const entry = parseFloat(trade.entryPrice);
        const tp = parseFloat(trade.takeProfit);
        const sl = parseFloat(trade.stopLoss);

        let reward, risk;
        if (trade.entryType === "buy") {
          reward = Math.abs(tp - entry);
          risk = Math.abs(entry - sl);
        } else {
          reward = Math.abs(entry - tp);
          risk = Math.abs(sl - entry);
        }

        return sum + reward / risk;
      }, 0) / trades.length;

    return [
      {
        title: "Overall Performance",
        content: `${trades.length} total trades with ${
          winningTrades.length
        } wins and ${
          losingTrades.length
        } losses (${winRate}% win rate). Average win: $${averageWin.toFixed(
          2
        )}, Average loss: $${averageLoss.toFixed(
          2
        )}. Best winning streak: ${maxWinStreak} trades. Profit factor: ${profitFactor}. Average RR ratio: ${averageRR.toFixed(
          2
        )}.`,
      },
      {
        title: "Trading Style Analysis",
        content: `Buy Trades: ${buyTrades.length} (${buyWinRate.toFixed(
          2
        )}% success), Sell Trades: ${sellTrades.length} (${sellWinRate.toFixed(
          2
        )}% success). ${
          Math.abs(buyTrades.length - sellTrades.length) <= trades.length * 0.2
            ? "Your trading shows a balanced approach between buys and sells."
            : buyTrades.length > sellTrades.length
            ? "You show a preference for buy trades."
            : "You show a preference for sell trades."
        } ${
          buyWinRate > sellWinRate
            ? `Buy trades are more successful with ${buyWinRate.toFixed(
                2
              )}% win rate vs ${sellWinRate.toFixed(2)}% for sells.`
            : `Sell trades are more successful with ${sellWinRate.toFixed(
                2
              )}% win rate vs ${buyWinRate.toFixed(2)}% for buys.`
        }`,
      },
      {
        title: "Risk Management",
        content: `Risk control shows ${
          (losingTrades.length / trades.length) * 100 <= 30
            ? "good discipline"
            : "room for improvement"
        }. Average win/loss ratio: ${(averageWin / (averageLoss || 1)).toFixed(
          2
        )}. ${
          averageWin > averageLoss * 1.5
            ? "Your winning trades are significantly larger than your losses, showing good risk management."
            : "Consider adjusting your take profit and stop loss levels to improve the win/loss ratio."
        }`,
      },
      {
        title: "Account Growth",
        content: `Starting balance: $${initialDeposit.toFixed(
          2
        )}, Current balance: $${calculateCurrentBalance().toFixed(2)} (${
          totalProfit >= 0 ? "+" : ""
        }${((totalProfit / initialDeposit) * 100).toFixed(2)}% ${
          totalProfit >= 0 ? "gain" : "loss"
        }). Largest winning trade: $${Math.max(
          ...winningTrades.map((t) => calculateProfit(t))
        ).toFixed(2)}, Largest losing trade: $${Math.abs(
          Math.min(...losingTrades.map((t) => calculateProfit(t)))
        ).toFixed(2)}.`,
      },
    ];
  };

  const generateImprovementSuggestions = () => {
    const winRate = parseFloat(calculateWinRate());
    const buyTrades = trades.filter((trade) => trade.entryType === "buy");
    const sellTrades = trades.filter((trade) => trade.entryType === "sell");
    const winningTrades = trades.filter((trade) => trade.outcome === "tp");
    const losingTrades = trades.filter((trade) => trade.outcome === "sl");

    const buyWinRate =
      (buyTrades.filter((trade) => trade.outcome === "tp").length /
        (buyTrades.length || 1)) *
      100;
    const sellWinRate =
      (sellTrades.filter((trade) => trade.outcome === "tp").length /
        (sellTrades.length || 1)) *
      100;
    const totalProfit = calculateCurrentBalance() - initialDeposit;

    // Calculate average trade metrics
    const avgWinAmount =
      winningTrades.reduce((sum, trade) => sum + calculateProfit(trade), 0) /
      (winningTrades.length || 1);
    const avgLossAmount =
      Math.abs(
        losingTrades.reduce((sum, trade) => sum + calculateProfit(trade), 0)
      ) / (losingTrades.length || 1);
    const riskRewardRatio = avgWinAmount / (avgLossAmount || 1);

    // Calculate lot size metrics
    const lotSizes = trades.map((trade) => parseFloat(trade.lotSize));
    const avgLotSize =
      lotSizes.reduce((sum, size) => sum + size, 0) / trades.length;
    const maxLotSize = Math.max(...lotSizes);
    const minLotSize = Math.min(...lotSizes);
    const lotSizeVariation = ((maxLotSize - minLotSize) / maxLotSize) * 100;

    // Calculate consecutive trade patterns
    let maxConsecutiveLosses = 0;
    let currentConsecutiveLosses = 0;
    trades.forEach((trade) => {
      if (trade.outcome === "sl") {
        currentConsecutiveLosses++;
        maxConsecutiveLosses = Math.max(
          maxConsecutiveLosses,
          currentConsecutiveLosses
        );
      } else {
        currentConsecutiveLosses = 0;
      }
    });

    return [
      {
        title: "Strategy Strengths",
        items: [
          trades.length >= 20
            ? `✓ Good sample size with ${trades.length} trades for statistical significance`
            : `✓ Conservative approach with ${trades.length} trades, minimizing exposure`,
          winRate >= 50
            ? `✓ Strong win rate at ${winRate.toFixed(2)}% (${
                winningTrades.length
              } wins out of ${trades.length} trades)`
            : `✓ Building experience with ${winningTrades.length} successful trades`,
          Math.abs(buyWinRate - sellWinRate) <= 20
            ? `✓ Balanced performance: Buy ${buyWinRate.toFixed(
                2
              )}% vs Sell ${sellWinRate.toFixed(2)}% win rates`
            : `✓ Specialized in ${
                buyWinRate > sellWinRate ? "buy" : "sell"
              } trades with ${Math.max(buyWinRate, sellWinRate).toFixed(
                2
              )}% win rate`,
          totalProfit > 0
            ? `✓ Profitable strategy with $${totalProfit.toFixed(2)} gain (${(
                (totalProfit / initialDeposit) *
                100
              ).toFixed(2)}% ROI)`
            : `✓ Risk management kept losses to ${(
                (Math.abs(totalProfit) / initialDeposit) *
                100
              ).toFixed(2)}% of initial capital`,
          riskRewardRatio >= 1.5
            ? `✓ Strong risk-reward ratio at ${riskRewardRatio.toFixed(2)}`
            : `✓ Consistent trade execution with defined entry and exit points`,
        ],
      },
      {
        title: "Areas for Improvement",
        items: [
          winRate < 50
            ? `⚠ Current win rate (${winRate.toFixed(
                2
              )}%) needs improvement - Focus on ${
                Math.abs(buyWinRate - sellWinRate) > 10
                  ? buyWinRate > sellWinRate
                    ? "sell"
                    : "buy"
                  : "both"
              } trade setups`
            : `⚠ Optimize take-profit levels: Current R:R ratio is ${riskRewardRatio.toFixed(
                2
              )} (target > 2.0 for long-term profitability)`,
          trades.length < 20
            ? `⚠ Increase sample size: Current ${trades.length} trades insufficient for strategy validation`
            : `⚠ Analyze ${
                winningTrades.length
              } winning trades for common patterns in ${
                buyWinRate > sellWinRate ? "buy" : "sell"
              } setups`,
          lotSizeVariation > 20
            ? `⚠ Standardize position sizing: Current variation ${lotSizeVariation.toFixed(
                2
              )}% (${minLotSize.toFixed(2)}-${maxLotSize.toFixed(2)} lots)`
            : `⚠ Consider scaling positions based on win rate in high-probability setups`,
          maxConsecutiveLosses > 3
            ? `⚠ Address losing streaks: Maximum ${maxConsecutiveLosses} consecutive losses detected`
            : `⚠ Optimize recovery strategy after losses (current recovery rate: ${(
                (winningTrades.length / (losingTrades.length || 1)) *
                100
              ).toFixed(2)}%)`,
          riskRewardRatio < 1.5
            ? `⚠ Improve risk-reward ratio: Current ${riskRewardRatio.toFixed(
                2
              )} below target of 2.0`
            : `⚠ Fine-tune entry points to reduce average loss size ($${avgLossAmount.toFixed(
                2
              )})`,
        ],
      },
      {
        title: "Action Plan",
        items: [
          `1. ${
            winRate < 50
              ? `Implement strict validation criteria: Price action confirmation + ${
                  Math.abs(buyWinRate - sellWinRate) > 10
                    ? buyWinRate > sellWinRate
                      ? "sell"
                      : "buy"
                    : "both"
                } side market structure`
              : `Optimize take-profit levels: Increase from current $${avgWinAmount.toFixed(
                  2
                )} average win to target $${(avgWinAmount * 1.5).toFixed(2)}`
          }`,
          `2. ${
            trades.length < 20
              ? "Build larger sample size while maintaining strict risk management"
              : `Focus on ${
                  buyWinRate > sellWinRate ? "buy" : "sell"
                } setups with ${Math.max(buyWinRate, sellWinRate).toFixed(
                  2
                )}% success rate`
          }`,
          `3. ${
            lotSizeVariation > 20
              ? `Standardize position sizing to ${avgLotSize.toFixed(
                  2
                )} lots (±10% variation maximum)`
              : `Scale positions gradually: Increase by 25% on setups with >70% win rate`
          }`,
          `4. ${
            totalProfit < 0
              ? `Implement mandatory 1:${
                  riskRewardRatio < 1 ? "2" : (riskRewardRatio * 2).toFixed(1)
                } minimum risk-reward ratio`
              : `Scale winning strategy with current ${riskRewardRatio.toFixed(
                  2
                )} risk-reward ratio`
          }`,
          `5. ${
            maxConsecutiveLosses > 3
              ? `Implement circuit breaker: Stop trading after ${Math.floor(
                  maxConsecutiveLosses / 2
                )} consecutive losses`
              : "Review and journal each trade with setup screenshots and market conditions"
          }`,
          `6. ${
            Math.abs(buyWinRate - sellWinRate) > 20
              ? `Focus on ${
                  buyWinRate > sellWinRate ? "buy" : "sell"
                } setups (${Math.max(buyWinRate, sellWinRate).toFixed(
                  2
                )}% win rate)`
              : "Maintain balanced approach with current directional win rates"
          }`,
          `7. Track key metrics: Win rate (target: ${Math.max(
            winRate + 10,
            60
          ).toFixed(2)}%), R:R (target: ${Math.max(
            riskRewardRatio * 1.5,
            2
          ).toFixed(2)}), Max drawdown (limit: ${(
            (Math.abs(totalProfit) / initialDeposit) *
            100
          ).toFixed(2)}%)`,
        ],
      },
    ];
  };

  const generateDeepTradeAnalysis = () => {
    // Trade Type Analysis
    const buyTrades = trades.filter((trade) => trade.entryType === "buy");
    const sellTrades = trades.filter((trade) => trade.entryType === "sell");
    const winningTrades = trades.filter((trade) => trade.outcome === "tp");
    const losingTrades = trades.filter((trade) => trade.outcome === "sl");

    // Calculate pip values for trades
    const calculatePips = (trade) => {
      const entry = parseFloat(trade.entryPrice);
      const exitPrice =
        trade.outcome === "tp"
          ? parseFloat(trade.takeProfit)
          : parseFloat(trade.stopLoss);
      return Math.abs(exitPrice - entry) * 100;
    };

    // Winning trades analysis
    const winningBuyTrades = buyTrades.filter(
      (trade) => trade.outcome === "tp"
    );
    const winningSellTrades = sellTrades.filter(
      (trade) => trade.outcome === "tp"
    );
    const averageWinningPips =
      winningTrades.length > 0
        ? winningTrades.reduce((sum, trade) => sum + calculatePips(trade), 0) /
          winningTrades.length
        : 0;

    // Losing trades analysis
    const averageLosingPips =
      losingTrades.length > 0
        ? losingTrades.reduce((sum, trade) => sum + calculatePips(trade), 0) /
          losingTrades.length
        : 0;

    // Lot size analysis
    const lotSizes = trades.map((trade) => parseFloat(trade.lotSize));
    const averageLotSize =
      lotSizes.reduce((sum, size) => sum + size, 0) / trades.length;
    const maxLotSize = Math.max(...lotSizes);
    const minLotSize = Math.min(...lotSizes);

    // Consecutive trades analysis
    let currentWinStreak = 0;
    let currentLossStreak = 0;
    let maxWinStreak = 0;
    let maxLossStreak = 0;
    let bestWinStreakProfit = 0;
    let currentStreakProfit = 0;

    trades.forEach((trade, index) => {
      if (trade.outcome === "tp") {
        currentWinStreak++;
        currentLossStreak = 0;
        currentStreakProfit += calculateProfit(trade);

        if (currentWinStreak > maxWinStreak) {
          maxWinStreak = currentWinStreak;
          bestWinStreakProfit = currentStreakProfit;
        }
      } else {
        if (currentWinStreak > 0) currentStreakProfit = 0;
        currentLossStreak++;
        currentWinStreak = 0;
        maxLossStreak = Math.max(maxLossStreak, currentLossStreak);
      }
    });

    // Recovery analysis
    const recoveryRates = [];
    let lastLoss = null;
    trades.forEach((trade, index) => {
      if (trade.outcome === "sl") {
        lastLoss = calculateProfit(trade);
      } else if (lastLoss && trade.outcome === "tp") {
        recoveryRates.push(calculateProfit(trade) / Math.abs(lastLoss));
        lastLoss = null;
      }
    });
    const averageRecoveryRate =
      recoveryRates.length > 0
        ? recoveryRates.reduce((sum, rate) => sum + rate, 0) /
          recoveryRates.length
        : 0;

    return [
      {
        title: "🎯 Trade Execution Analysis",
        content: [
          `Win Rate by Direction: Buy ${(
            (winningBuyTrades.length / buyTrades.length) *
            100
          ).toFixed(2)}%, Sell ${(
            (winningSellTrades.length / sellTrades.length) *
            100
          ).toFixed(2)}%`,
          `Average Winning Trade: ${averageWinningPips.toFixed(0)} pips ($${(
            winningTrades.reduce(
              (sum, trade) => sum + calculateProfit(trade),
              0
            ) / winningTrades.length
          ).toFixed(2)})`,
          `Average Losing Trade: ${averageLosingPips.toFixed(0)} pips ($${(
            Math.abs(
              losingTrades.reduce(
                (sum, trade) => sum + calculateProfit(trade),
                0
              )
            ) / losingTrades.length
          ).toFixed(2)})`,
          `Most Successful Type: ${
            winningBuyTrades.length / buyTrades.length >
            winningSellTrades.length / sellTrades.length
              ? "Buy"
              : "Sell"
          } trades`,
        ],
      },
      {
        title: "💰 Position Sizing Patterns",
        content: [
          `Lot Size Range: ${minLotSize.toFixed(2)} - ${maxLotSize.toFixed(
            2
          )} lots`,
          `Average Lot Size: ${averageLotSize.toFixed(2)} lots`,
          `Position Size Variation: ${(
            ((maxLotSize - minLotSize) / maxLotSize) *
            100
          ).toFixed(2)}%`,
          `Average Position Value: $${(averageLotSize * 100).toFixed(2)}`,
        ],
      },
      {
        title: "📊 Streak Analysis",
        content: [
          `Longest Winning Streak: ${maxWinStreak} trades ($${bestWinStreakProfit.toFixed(
            2
          )} profit)`,
          `Longest Losing Streak: ${maxLossStreak} trades`,
          `Recovery Efficiency: ${(averageRecoveryRate * 100).toFixed(
            2
          )}% (Average profit after loss)`,
          `Consecutive Wins Impact: ${(
            ((maxWinStreak * averageWinningPips) /
              (trades.length * averageLosingPips)) *
            100
          ).toFixed(2)}% of total performance`,
        ],
      },
      {
        title: "📈 Risk Management Metrics",
        content: [
          `Risk-Reward Profile: ${(
            averageWinningPips / averageLosingPips
          ).toFixed(2)} (Reward/Risk)`,
          `Position Size Control: ${(
            ((maxLotSize - minLotSize) / maxLotSize) *
            100
          ).toFixed(2)}% variation`,
          `Loss Recovery Rate: ${(
            winningTrades.length / (losingTrades.length || 1)
          ).toFixed(2)} wins needed per loss`,
          `Win/Loss Distribution: ${(
            (winningTrades.length / (trades.length || 1)) *
            100
          ).toFixed(2)}% profitable trades`,
        ],
      },
    ];
  };

  return (
    <div className={`min-h-screen ${darkMode ? "bg-gray-900" : "bg-gray-100"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1
            className={`text-3xl font-bold ${
              darkMode ? "text-white" : "text-gray-800"
            }`}
          >
            Backtest Results
          </h1>
          <div className="flex gap-4">
            <button
              onClick={exportToPDF}
              className={`px-4 py-2 rounded text-white text-sm font-medium transition-colors duration-150 flex items-center gap-2
                ${
                  darkMode
                    ? "bg-indigo-600 hover:bg-indigo-700"
                    : "bg-indigo-500 hover:bg-indigo-600"
                }`}
            >
              <span className="text-xl">📄</span>
              Export PDF
            </button>
            <button
              onClick={handleReset}
              className={`px-4 py-2 rounded text-white text-sm font-medium transition-colors duration-150
                ${
                  darkMode
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-red-500 hover:bg-red-600"
                }`}
            >
              Reset Backtest
            </button>
          </div>
        </div>

        <div ref={contentRef} data-clone-content className="space-y-6">
          {/* Key Metrics */}
          <div
            className={`${
              darkMode ? "bg-gray-800" : "bg-white"
            } p-6 rounded-lg shadow-md`}
          >
            <h2
              className={`text-xl font-semibold mb-6 ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Key Metrics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="transform hover:scale-105 transition-transform duration-200 p-4 rounded-lg hover:shadow-lg">
                <p
                  className={`${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  } flex items-center gap-2`}
                >
                  <span className="text-xl">📊</span> Total Trades
                </p>
                <p
                  className={`text-2xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  } animate-fade-in`}
                >
                  {calculateTotalTrades()}
                </p>
              </div>
              <div className="transform hover:scale-105 transition-transform duration-200 p-4 rounded-lg hover:shadow-lg">
                <p
                  className={`${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  } flex items-center gap-2`}
                >
                  <span className="text-xl">🎯</span> Win Rate
                </p>
                <p
                  className={`text-2xl font-bold text-green-500 animate-fade-in`}
                >
                  {calculateWinRate()}%
                </p>
              </div>
              <div className="transform hover:scale-105 transition-transform duration-200 p-4 rounded-lg hover:shadow-lg">
                <p
                  className={`${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  } flex items-center gap-2`}
                >
                  <span className="text-xl">
                    {calculateCurrentBalance() > initialDeposit ? "💰" : "📉"}
                  </span>{" "}
                  Total Profit/Loss
                </p>
                <p
                  className={`text-2xl font-bold ${
                    calculateCurrentBalance() > initialDeposit
                      ? "text-green-500"
                      : "text-red-500"
                  } animate-fade-in`}
                >
                  ${(calculateCurrentBalance() - initialDeposit).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Strategy Analysis */}
          <div
            className={`${
              darkMode ? "bg-gray-800" : "bg-white"
            } p-6 rounded-lg shadow-md`}
          >
            <h2
              className={`text-xl font-semibold mb-6 ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Strategy Analysis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {generateStrategyAnalysis().map((section, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg ${
                    darkMode ? "bg-gray-700/50" : "bg-gray-50"
                  } 
                    transform hover:scale-102 transition-all duration-200 hover:shadow-lg
                    ${
                      index === 0
                        ? "animate-slide-in-left"
                        : "animate-slide-in-right"
                    }`}
                >
                  <h3
                    className={`text-lg font-medium mb-2 ${
                      darkMode ? "text-white" : "text-gray-800"
                    } flex items-center gap-2`}
                  >
                    <span className="text-xl">
                      {section.title === "Overall Performance" && "📈"}
                      {section.title === "Trading Style Analysis" && "🎭"}
                      {section.title === "Risk Management" && "🛡️"}
                      {section.title === "Account Growth" && "💹"}
                    </span>
                    {section.title}
                  </h3>
                  <p
                    className={`${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Improvement Suggestions */}
          <div
            className={`${
              darkMode ? "bg-gray-800" : "bg-white"
            } p-6 rounded-lg shadow-md`}
          >
            <h2
              className={`text-xl font-semibold mb-6 ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Improvement Suggestions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {generateImprovementSuggestions().map((section, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg ${
                    darkMode ? "bg-gray-700/50" : "bg-gray-50"
                  }
                    transform hover:scale-102 transition-all duration-200 hover:shadow-lg
                    ${
                      index === 0
                        ? "animate-slide-in-left"
                        : index === 1
                        ? "animate-slide-in-up"
                        : "animate-slide-in-right"
                    }`}
                >
                  <h3
                    className={`text-lg font-medium mb-3 ${
                      darkMode ? "text-white" : "text-gray-800"
                    } flex items-center gap-2`}
                  >
                    <span className="text-xl">
                      {section.title === "Strategy Strengths" && "💪"}
                      {section.title === "Areas for Improvement" && "🎯"}
                      {section.title === "Action Plan" && "📋"}
                    </span>
                    {section.title}
                  </h3>
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li
                        key={itemIndex}
                        className={`flex items-start ${
                          darkMode ? "text-gray-300" : "text-gray-600"
                        }
                          hover:translate-x-1 transition-transform duration-200`}
                      >
                        <span className="mr-2">
                          {item.startsWith("✓")
                            ? "✅"
                            : item.startsWith("⚠")
                            ? "⚠️"
                            : "📌"}
                        </span>
                        <span>{item.replace(/^[✓⚠]\s/, "")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Deep Trade Analysis */}
          <div
            className={`${
              darkMode ? "bg-gray-800" : "bg-white"
            } p-6 rounded-lg shadow-md`}
          >
            <h2
              className={`text-xl font-semibold mb-6 flex items-center gap-2 ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              <span className="text-2xl">🔍</span> Deep Trade Analysis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {generateDeepTradeAnalysis().map((section, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg ${
                    darkMode ? "bg-gray-700/50" : "bg-gray-50"
                  }
                    transform hover:scale-102 transition-all duration-200 hover:shadow-lg
                    ${
                      index % 2 === 0
                        ? "animate-slide-in-left"
                        : "animate-slide-in-right"
                    }`}
                >
                  <h3
                    className={`text-lg font-medium mb-4 ${
                      darkMode ? "text-white" : "text-gray-800"
                    } flex items-center gap-2`}
                  >
                    <span className="text-xl">
                      {section.title.split(" ")[0]}
                    </span>
                    <span>{section.title.split(" ").slice(1).join(" ")}</span>
                  </h3>
                  <ul className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <li
                        key={itemIndex}
                        className={`flex items-start ${
                          darkMode ? "text-gray-300" : "text-gray-600"
                        }
                          hover:translate-x-1 transition-transform duration-200`}
                      >
                        <span className="mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Add AI Analysis component before the Statistics component */}
          <AIAnalysis
            trades={trades}
            initialDeposit={initialDeposit}
            calculateCurrentBalance={calculateCurrentBalance}
          />

          {/* Detailed Statistics */}
          <Statistics
            initialDeposit={initialDeposit}
            calculateCurrentBalance={calculateCurrentBalance}
          />

          {/* Trade History Summary */}
          <div
            className={`${
              darkMode ? "bg-gray-800" : "bg-white"
            } p-6 rounded-lg shadow-md`}
          >
            <h2
              className={`text-xl font-semibold mb-6 flex items-center gap-2 ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              <span className="text-2xl">📊</span> Trade Summary
            </h2>
            <div className="overflow-x-auto rounded-lg">
              <table className="w-full border-collapse">
                <thead>
                  <tr
                    className={`${darkMode ? "bg-gray-700/50" : "bg-gray-50"}`}
                  >
                    <th
                      className={`p-4 text-left font-semibold ${
                        darkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">💱</span>
                        <span>Trade Type</span>
                      </div>
                    </th>
                    <th
                      className={`p-4 text-left font-semibold ${
                        darkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🔢</span>
                        <span>Count</span>
                      </div>
                    </th>
                    <th
                      className={`p-4 text-left font-semibold ${
                        darkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">📈</span>
                        <span>Success Rate</span>
                      </div>
                    </th>
                    <th
                      className={`p-4 text-left font-semibold ${
                        darkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">💵</span>
                        <span>Avg. Profit</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    className={`border-t ${
                      darkMode ? "border-gray-700" : "border-gray-200"
                    } 
                      hover:bg-opacity-50 ${
                        darkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                      } 
                      transition-colors duration-200`}
                  >
                    <td
                      className={`p-4 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">📈</span>
                        <span className="font-medium">Buy Trades</span>
                      </div>
                    </td>
                    <td
                      className={`p-4 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <span className="font-medium">
                        {
                          trades.filter((trade) => trade.entryType === "buy")
                            .length
                        }
                      </span>
                    </td>
                    <td className={`p-4`}>
                      {(() => {
                        const winRate =
                          (trades.filter(
                            (trade) =>
                              trade.entryType === "buy" &&
                              trade.outcome === "tp"
                          ).length /
                            (trades.filter((trade) => trade.entryType === "buy")
                              .length || 1)) *
                          100;
                        return (
                          <span
                            className={`font-medium ${
                              winRate >= 50 ? "text-green-500" : "text-red-500"
                            }`}
                          >
                            {winRate.toFixed(2)}%
                          </span>
                        );
                      })()}
                    </td>
                    <td className={`p-4`}>
                      {(() => {
                        const buyTrades = trades.filter(
                          (trade) => trade.entryType === "buy"
                        );
                        const avgProfit =
                          buyTrades.reduce((acc, trade) => {
                            const profit =
                              trade.outcome === "tp"
                                ? (trade.takeProfit - trade.entryPrice) *
                                  trade.lotSize
                                : (trade.stopLoss - trade.entryPrice) *
                                  trade.lotSize;
                            return acc + profit;
                          }, 0) / (buyTrades.length || 1);
                        return (
                          <span
                            className={`font-medium ${
                              avgProfit >= 0 ? "text-green-500" : "text-red-500"
                            }`}
                          >
                            ${avgProfit.toFixed(2)}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                  <tr
                    className={`border-t ${
                      darkMode ? "border-gray-700" : "border-gray-200"
                    } 
                      hover:bg-opacity-50 ${
                        darkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                      } 
                      transition-colors duration-200`}
                  >
                    <td
                      className={`p-4 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">📉</span>
                        <span className="font-medium">Sell Trades</span>
                      </div>
                    </td>
                    <td
                      className={`p-4 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <span className="font-medium">
                        {
                          trades.filter((trade) => trade.entryType === "sell")
                            .length
                        }
                      </span>
                    </td>
                    <td className={`p-4`}>
                      {(() => {
                        const winRate =
                          (trades.filter(
                            (trade) =>
                              trade.entryType === "sell" &&
                              trade.outcome === "tp"
                          ).length /
                            (trades.filter(
                              (trade) => trade.entryType === "sell"
                            ).length || 1)) *
                          100;
                        return (
                          <span
                            className={`font-medium ${
                              winRate >= 50 ? "text-green-500" : "text-red-500"
                            }`}
                          >
                            {winRate.toFixed(2)}%
                          </span>
                        );
                      })()}
                    </td>
                    <td className={`p-4`}>
                      {(() => {
                        const sellTrades = trades.filter(
                          (trade) => trade.entryType === "sell"
                        );
                        const avgProfit =
                          sellTrades.reduce((acc, trade) => {
                            const profit =
                              trade.outcome === "tp"
                                ? (trade.entryPrice - trade.takeProfit) *
                                  trade.lotSize
                                : (trade.entryPrice - trade.stopLoss) *
                                  trade.lotSize;
                            return acc + profit;
                          }, 0) / (sellTrades.length || 1);
                        return (
                          <span
                            className={`font-medium ${
                              avgProfit >= 0 ? "text-green-500" : "text-red-500"
                            }`}
                          >
                            ${avgProfit.toFixed(2)}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BacktestResults;
